// src/contexts/EmployeeContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import employeeService from '../services/employee.service';
import approvalService from '../services/approval.service';
import companySettingsService from '../services/companySettings.service';
import { useAuth } from '../hooks/useAuth';
import { sessionCache } from '../utils/sessionCache';
import { evaluateEmployeeLimit, getCompanyFromStorage, hasSubscriptionPlan } from '../utils/subscriptionPlan';

const EmployeeContext = createContext();

export const useEmployees = () => {
    const context = useContext(EmployeeContext);
    if (!context) {
        throw new Error('useEmployees must be used within an EmployeeProvider');
    }
    return context;
};

// Removed localStorage utilities - all data comes from backend only

export const EmployeeProvider = ({ children }) => {
    const { user, company } = useAuth();
    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentEmployee, setCurrentEmployee] = useState(null);
    const employeeUpdateCallbacks = useRef(new Set());

    const getCurrentUser = () => {
        if (user) {
            return user;
        }
        try {
            const storedUser = localStorage.getItem('user');
            return storedUser ? JSON.parse(storedUser) : null;
        } catch (error) {
            console.error('Error parsing stored user data:', error);
            return null;
        }
    };

    const getCurrentCompany = () => {
        if (company) {
            return company;
        }
        return getCompanyFromStorage();
    };

    const getEmployeeAddRestriction = (addingCount = 1) => {
        return evaluateEmployeeLimit({
            company: getCurrentCompany(),
            currentEmployeesCount: employees.length,
            addingCount
        });
    };

    const enforceEmployeeLimitWithFreshCount = async (addingCount = 1) => {
        let companyData = getCurrentCompany();
        const authToken = localStorage.getItem('authToken');

        // Authoritative source: companies table via backend profile endpoint
        if (authToken) {
            try {
                const companyFromTable = await companySettingsService.getCompanyFromCompaniesTable();
                const backendCompany = await companySettingsService.getCompanyProfile();

                companyData = {
                    ...companyData,
                    ...(backendCompany && Object.keys(backendCompany).length > 0 ? backendCompany : {}),
                    ...(companyFromTable && Object.keys(companyFromTable).length > 0 ? companyFromTable : {})
                };

                if (!hasSubscriptionPlan(companyData)) {
                    throw new Error('Unable to validate subscription plan. Please refresh and try again.');
                }
            } catch (error) {
                console.error('Error validating company subscription plan for trial restriction:', error);
                throw error;
            }
        }

        const baseRestriction = evaluateEmployeeLimit({
            company: companyData,
            currentEmployeesCount: employees.length,
            addingCount
        });

        // For both trial and paid plans, check backend subscription limit
        if (authToken) {
            try {
                const { canAddEmployee } = await import('../services/razorpay.service');
                const limitCheck = await canAddEmployee();

                if (limitCheck.planType === 'trial') {
                    // Trial: use existing trial logic with fresh count
                    let employeeCount = employees.length;
                    try {
                        const freshEmployees = await employeeService.getAllEmployees();
                        employeeCount = Array.isArray(freshEmployees) ? freshEmployees.length : employeeCount;
                    } catch (e) {
                        console.error('Error fetching fresh employee count:', e);
                    }

                    const restriction = evaluateEmployeeLimit({
                        company: companyData,
                        currentEmployeesCount: employeeCount,
                        addingCount
                    });

                    if (!restriction.allowed) {
                        throw new Error(restriction.message);
                    }
                    return restriction;
                }

                // Paid plan: check against subscription employee_limit
                const wouldExceed = (limitCheck.currentCount + addingCount) > limitCheck.limit;
                if (wouldExceed) {
                    throw new Error(`Your ${limitCheck.planType} plan allows up to ${limitCheck.limit} employees (currently ${limitCheck.currentCount}). Upgrade your plan to add more.`);
                }

                return { allowed: true, isTrial: false, limit: limitCheck.limit, remaining: limitCheck.limit - limitCheck.currentCount };
            } catch (error) {
                if (error.message && (error.message.includes('plan allows') || error.message.includes('Free trial'))) {
                    throw error;
                }
                console.error('Error checking subscription limit:', error);
                // Fallback: allow if backend check fails
                return { allowed: true };
            }
        }

        // No auth token — use local trial check
        if (!baseRestriction.isTrial) {
            return { allowed: true };
        }

        if (!baseRestriction.allowed) {
            throw new Error(baseRestriction.message);
        }

        return baseRestriction;
    };

    const checkEmployeeAddRestriction = async (addingCount = 1) => {
        return enforceEmployeeLimitWithFreshCount(addingCount);
    };

    const getNormalizedRole = (currentUser) => {
        if (!currentUser) return '';
        if (currentUser.is_admin) return 'admin';

        const rawRole = currentUser.role
            || currentUser.role_name
            || currentUser.accessLevel
            || currentUser.access_level
            || '';

        return rawRole.toString().toLowerCase().replace(/\s+/g, '');
    };

    const areValuesEqual = (previousValue, nextValue) => {
        const isObject = (value) => value !== null && typeof value === 'object';

        if (isObject(previousValue) || isObject(nextValue)) {
            try {
                return JSON.stringify(previousValue ?? null) === JSON.stringify(nextValue ?? null);
            } catch (error) {
                console.error('Error comparing complex values for change summary:', error);
                return false;
            }
        }

        return (previousValue ?? '') === (nextValue ?? '');
    };

    const formatFieldLabel = (field) => {
        if (!field) return '';

        const LABEL_OVERRIDES = {
            first_name: 'First Name',
            middle_name: 'Middle Name',
            last_name: 'Last Name',
            display_name: 'Display Name',
            personal_email: 'Personal Email',
            email: 'Email',
            phone: 'Phone',
            date_of_birth: 'Date of Birth',
            joining_date: 'Joining Date',
            department_id: 'Department',
            department: 'Department',
            designation: 'Designation',
            employment_status: 'Employment Status',
            employment_type: 'Employment Type',
            managerId: 'Reporting Manager',
            reporting_manager_id: 'Reporting Manager',
            secondaryAccess: 'Secondary Access'
        };

        if (LABEL_OVERRIDES[field]) {
            return LABEL_OVERRIDES[field];
        }

        const withSpaces = field
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/_/g, ' ');

        return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
    };

    const APPROVAL_ALLOWED_FIELDS = new Set([
        'first_name',
        'middle_name',
        'last_name',
        'display_name',
        'email',
        'work_email',
        'personal_email',
        'phone',
        'date_of_birth',
        'gender',
        'marital_status',
        'nationality',
        'country',
        'state',
        'city',
        'address',
        'postal_code',
        'joining_date',
        'exit_date',
        'employment_status',
        'employment_type',
        'designation',
        'department_id',
        'department',
        'managerId',
        'reporting_manager_id',
        'secondary_access',
        'secondaryAccess',
        'work_location',
        'emergency_contact_name',
        'emergency_contact_relationship',
        'emergency_contact_phone',
        'bank_name',
        'bank_account_number',
        'bank_ifsc',
        'photo'
    ]);

    const buildChangeSummary = (existingEmployee, updates, allowedFields = null) => {
        if (!updates || typeof updates !== 'object') {
            return [];
        }

        return Object.entries(updates).reduce((summary, [field, nextValue]) => {
            if (allowedFields && !allowedFields.has(field)) {
                return summary;
            }

            const previousValue = existingEmployee ? existingEmployee[field] : undefined;

            if (areValuesEqual(previousValue, nextValue)) {
                return summary;
            }

            summary.push({
                field,
                label: formatFieldLabel(field),
                from: previousValue,
                to: nextValue
            });
            return summary;
        }, []);
    };

    // Fetch departments from backend
    const fetchDepartments = async () => {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
            //console.log('⚠️ No auth token, using default departments');
            // Fallback to default departments
            setDepartments([
                { id: 'HR', name: 'Human Resources' },
                { id: 'ENG', name: 'Engineering' },
                { id: 'FIN', name: 'Finance' },
                { id: 'SALES', name: 'Sales' },
                { id: 'MKT', name: 'Marketing' }
            ]);
            return;
        }

        try {
            const data = await employeeService.getDepartments();
            if (data && data.length > 0) {
                setDepartments(data);
            } else {
                // Fallback to default if backend returns empty
                setDepartments([
                    { id: 'HR', name: 'Human Resources' },
                    { id: 'ENG', name: 'Engineering' },
                    { id: 'FIN', name: 'Finance' },
                    { id: 'SALES', name: 'Sales' },
                    { id: 'MKT', name: 'Marketing' }
                ]);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
            // Fallback to default departments
            setDepartments([
                { id: 'HR', name: 'Human Resources' },
                { id: 'ENG', name: 'Engineering' },
                { id: 'FIN', name: 'Finance' },
                { id: 'SALES', name: 'Sales' },
                { id: 'MKT', name: 'Marketing' }
            ]);
        }
    };

    // Fetch employees from backend when authenticated
    const fetchEmployees = async (forceRefresh = false) => {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
            //console.log('⚠️ No auth token, cannot fetch employees');
            setEmployees([]);
            return;
        }

        // Check cache first if not forcing refresh
        if (!forceRefresh) {
            const cached = sessionCache.get('employees');
            if (cached) {
                //console.log('📦 Using cached employees data');
                setEmployees(cached);
                return;
            }
        }

        try {
            setLoading(true);
            const data = await employeeService.getAllEmployees();

            //console.log('📥 Received employees data from backend, count:', data.length);
            if (data.length > 0) {
                //console.log('🔍 Sample employee from backend:', data[0]);
                //console.log('🔍 leave_plan in first employee:', data[0].leave_plan);
                //console.log('🔍 role_name in first employee:', data[0].role_name);
                //console.log('🔍 role_id in first employee:', data[0].role_id);
                //console.log('🔍 access_level in first employee:', data[0].access_level);
            }

            // Map backend format to frontend format
            const mappedEmployees = data.map(emp => ({
                ...emp, // Include all other backend fields first
                id: emp.id,
                employee_code: emp.employee_code,
                name: `${emp.first_name} ${emp.last_name}`,
                first_name: emp.first_name,
                middle_name: emp.middle_name,
                last_name: emp.last_name,
                email: emp.email || emp.work_email,
                phone: emp.phone,
                department: emp.department_name || emp.department,
                designation: emp.designation,
                employment_status: emp.employment_status,
                employment_type: emp.employment_type,
                joining_date: emp.joining_date,
                role_name: emp.role_name,
                // Map database fields to frontend field names
                // Use role_name from joined roles table
                // Normalize: remove spaces, convert to lowercase to match dropdown values
                // "Super Admin" -> "superadmin" -> "admin", "Manager" -> "manager", "Employee" -> "employee"
                accessLevel: emp.role_name ?
                    (() => {
                        const normalized = emp.role_name.toLowerCase().replace(/\s+/g, '');
                        // Map "superadmin" to "admin"
                        if (normalized === 'superadmin' || normalized === 'admin') return 'admin';
                        if (normalized === 'manager') return 'manager';
                        return 'employee'; // Default for any other role
                    })()
                    : (emp.access_level || 'employee'),
                secondaryAccess: emp.secondary_access || 'employee',
                payPackage: Array.isArray(emp.pay_package) ? emp.pay_package : [], // Ensure it's an array, not object
                pay_package: emp.pay_package, // Keep original object structure for other components
                managerId: emp.reporting_manager_id || null, // Map backend reporting_manager_id to frontend managerId (must be last to not be overwritten)
                leave_plan: emp.leave_plan, // Explicitly preserve leave_plan field
                holiday_plan_id: emp.holiday_plan_id, // Explicitly preserve holiday_plan_id field
                // Explicitly preserve tax/identity fields for payslip generation
                pan_number: emp.pan_number || '',
                uan_number: emp.uan_number || '',
                uan: emp.uan || emp.uan_number || ''
            }));

            //console.log('🔍 After mapping, first employee:', mappedEmployees[0]);
            //console.log('🔍 leave_plan in mapped employee:', mappedEmployees[0]?.leave_plan);
            //console.log('🔍 accessLevel in mapped employee:', mappedEmployees[0]?.accessLevel);
            //console.log('🔍 role_name in mapped employee:', mappedEmployees[0]?.role_name);

            setEmployees(mappedEmployees);
            // Cache the data
            sessionCache.set('employees', mappedEmployees);
            //console.log('💾 Cached employees data');
        } catch (error) {
            console.error('Error fetching employees:', error);
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch departments and employees on mount (only if authenticated)
    useEffect(() => {
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
            fetchDepartments();
            fetchEmployees();
        }
    }, []);

    // Generate unique employee ID
    const generateEmployeeId = () => {
        if (employees.length === 0) {
            return 'EMP001';
        }
        const existingIds = employees.map(emp => parseInt(emp.id.replace('EMP', '')));
        const maxId = Math.max(...existingIds);
        return `EMP${String(maxId + 1).padStart(3, '0')}`;
    };

    // Notify all registered callbacks about employee updates
    const notifyEmployeeUpdate = useCallback((action, employeeData) => {
        employeeUpdateCallbacks.current.forEach(callback => {
            try {
                callback(action, employeeData);
            } catch (error) {
                console.error('Error in employee update callback:', error);
            }
        });
    }, []);

    // Register callback for employee updates
    const onEmployeeUpdate = useCallback((callback) => {
        employeeUpdateCallbacks.current.add(callback);
        return () => {
            employeeUpdateCallbacks.current.delete(callback);
        };
    }, []);

    // Add new employee
    const addEmployee = async (employeeData) => {
        await enforceEmployeeLimitWithFreshCount(1);

        const authToken = localStorage.getItem('authToken');
        // Find manager by name - since dropdown now shows all employees
        const manager = employees.find(emp => emp.name === employeeData.reportingManager);

        // Prepare backend-compatible data with ALL wizard fields
        const backendData = {
            // Basic Details (Step 1)
            first_name: employeeData.firstName,
            middle_name: employeeData.middleName || null,
            last_name: employeeData.lastName,
            display_name: employeeData.displayName || null,
            email: employeeData.workEmail,
            employee_code: employeeData.employeeNumber || generateEmployeeId(),
            phone: employeeData.mobileNumber ? `${employeeData.countryCode} ${employeeData.mobileNumber}` : '',
            date_of_birth: employeeData.dateOfBirth || null,
            gender: employeeData.gender || null,
            nationality: employeeData.nationality || null,
            country: employeeData.workCountry || employeeData.nationality || null,

            // Job Details (Step 2)
            legal_entity: employeeData.legalEntity || null,
            department_id: employeeData.department,
            designation: employeeData.jobTitle,
            work_location: employeeData.location || null,
            joining_date: employeeData.joiningDate,
            employment_type: employeeData.timeType?.toLowerCase().replace(' ', '-') || 'full-time',
            reporting_manager_id: manager ? manager.id : null,
            role_id: employeeData.role_id || null,
            probation_policy: employeeData.probationPolicy && employeeData.probationPolicy !== 'Default Probation Policy'
                ? employeeData.probationPolicy
                : null,  // Fixed: probation_policy is VARCHAR, not probation_period_months
            notice_period: employeeData.noticePeriod && employeeData.noticePeriod !== 'Default Notice Period'
                ? employeeData.noticePeriod
                : null,

            // Work Details (Step 3)
            holiday_plan_id: employeeData.holidayList || null,
            shift_id: employeeData.shift_id || employeeData.shift || null,
            weekly_off: employeeData.weeklyOff || null,
            attendance_number: employeeData.attendanceNumber || null,
            attendance_capture_scheme: employeeData.attendanceCaptureScheme || null,
            attendance_tracking_policy: employeeData.attendanceTrackingPolicy || null,
            leave_plan: employeeData.leavePlan || null,
            overtime_policy: employeeData.overtime || null,

            // Compensation (Step 4)
            ctc: employeeData.ctc ? parseFloat(employeeData.ctc) : null,
            currency: employeeData.currency || 'INR',
            effective_date: employeeData.effectiveDate || null,
            payroll_frequency: employeeData.payrollFrequency || 'Monthly',

            // Bank Details (Step 4)
            bankName: employeeData.bankName || null,
            accountNumber: employeeData.accountNumber || null,
            ifscCode: employeeData.ifscCode || null,
            branchName: employeeData.branchName || null,

            // Tax Information (Step 4)
            panNumber: employeeData.panNumber || null,
            uanNumber: employeeData.uanNumber || null,

            // Salary Structure (Step 4)
            basicSalary: employeeData.basicSalary ? parseFloat(employeeData.basicSalary) : null,
            hra: employeeData.hra ? parseFloat(employeeData.hra) : null,
            specialAllowance: employeeData.specialAllowance ? parseFloat(employeeData.specialAllowance) : null,
            variablePay: employeeData.variablePay ? parseFloat(employeeData.variablePay) : null,

            // Store detailed compensation in pay_package JSONB field - MATCH PROFILE UPDATE STRUCTURE
            pay_package: employeeData.ctc ? {
                ctc: parseFloat(employeeData.ctc),
                currency: employeeData.currency || 'INR',
                annual_ctc: parseFloat(employeeData.ctc),
                effective_date: employeeData.effectiveDate || new Date().toISOString(),
                salary_components: [
                    // Earnings
                    ...(employeeData.basicSalary ? [{
                        name: 'Basic Salary',
                        type: 'earning',
                        amount: employeeData.basicSalary,
                        category: 'fixed',
                        is_custom: false,
                        monthly_amount: Math.round(parseFloat(employeeData.basicSalary) / 12)
                    }] : []),
                    ...(employeeData.hra ? [{
                        name: 'HRA (House Rent Allowance)',
                        type: 'earning',
                        amount: employeeData.hra,
                        category: 'allowance',
                        is_custom: false,
                        monthly_amount: Math.round(parseFloat(employeeData.hra) / 12)
                    }] : []),
                    ...(employeeData.specialAllowance ? [{
                        name: 'Special Allowance',
                        type: 'earning',
                        amount: employeeData.specialAllowance,
                        category: 'allowance',
                        is_custom: false,
                        monthly_amount: Math.round(parseFloat(employeeData.specialAllowance) / 12)
                    }] : []),
                    ...(employeeData.variablePay ? [{
                        name: 'Variable Pay',
                        type: 'earning',
                        amount: employeeData.variablePay,
                        category: 'variable',
                        is_custom: false,
                        monthly_amount: Math.round(parseFloat(employeeData.variablePay) / 12)
                    }] : [])
                ]
            } : null
        };

        // Try backend API first
        if (authToken) {
            let createdEmployee;
            try {
                createdEmployee = await employeeService.addEmployee(backendData);
            } catch (error) {
                console.error('❌ Backend addEmployee FAILED:', error.response?.data || error.message);
                const backendMsg = error.response?.data?.message || error.response?.data?.error || error.message;
                throw new Error(backendMsg || 'Failed to add employee');
            }

            // Employee created — refresh list (errors here are non-fatal)
            try {
                await fetchEmployees(true);
            } catch (e) {
                console.warn('Post-create refresh failed (non-fatal):', e.message);
            }

            notifyEmployeeUpdate('added', createdEmployee);
            return createdEmployee;
        }

        // Fallback to localStorage
        const newId = employeeData.employeeNumber || generateEmployeeId();
        const newEmployee = {
            id: newId,
            name: `${employeeData.firstName} ${employeeData.lastName}`,
            first_name: employeeData.firstName,
            middle_name: employeeData.middleName || '',
            last_name: employeeData.lastName,
            display_name: employeeData.displayName,
            employee_code: newId,
            gender: employeeData.gender,
            dateOfBirth: employeeData.dateOfBirth,
            email: employeeData.workEmail,
            work_email: employeeData.workEmail,
            phone: employeeData.mobileNumber ? `${employeeData.countryCode} ${employeeData.mobileNumber}` : '',
            department: employeeData.department,
            department_id: employeeData.department,
            designation: employeeData.jobTitle,
            joining_date: employeeData.joiningDate,
            employment_status: 'active',
            employment_type: employeeData.timeType?.toLowerCase().replace(' ', '-') || 'full-time',
            managerId: manager ? manager.id : null,
            managerName: employeeData.reportingManager || null,
            role: 'employee',
            photo: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 50) + 1}`
        };

        setEmployees(prev => {
            const updated = [...prev, newEmployee];
            // Notify all components about the new employee
            notifyEmployeeUpdate('added', newEmployee);
            return updated;
        });
        return newEmployee;
    };

    // Bulk add employees
    const bulkAddEmployees = async (employeesData) => {
        await enforceEmployeeLimitWithFreshCount(employeesData.length);

        const authToken = localStorage.getItem('authToken');
        
        // Try backend API first if authenticated
        if (authToken) {
            try {
                console.log('🚀 Calling backend API to bulk import employees...');
                
                // Transform the imported data to match backend format
                const transformedEmployees = employeesData.map(employeeData => {
                    // Find manager by name if manager_id is provided
                    const manager = employees.find(emp => emp.id === employeeData.manager_id);
                    
                    return {
                        // Basic Details
                        first_name: employeeData.first_name,
                        last_name: employeeData.last_name,
                        email: employeeData.email,
                        employee_code: employeeData.employee_code || `EMP${String(employees.length + 1000).padStart(4, '0')}`,
                        phone: employeeData.phone || '',
                        
                        // Job Details
                        department_id: employeeData.department_id,
                        designation: employeeData.designation,
                        work_location: employeeData.work_location || null,
                        joining_date: employeeData.joining_date,
                        employment_type: employeeData.employment_type?.toLowerCase().replace(' ', '-') || 'full-time',
                        reporting_manager_id: employeeData.manager_id || null,
                        
                        // Additional fields (optional)
                        middle_name: employeeData.middle_name || null,
                        display_name: employeeData.display_name || null,
                        gender: employeeData.gender || null,
                        nationality: employeeData.nationality || null,
                        date_of_birth: employeeData.date_of_birth || null,
                        country: employeeData.country || null,
                        state: employeeData.state || null,
                        city: employeeData.city || null,
                        address: employeeData.address || null,
                        postal_code: employeeData.postal_code || null,
                        emergency_contact_name: employeeData.emergency_contact_name || null,
                        emergency_contact_relationship: employeeData.emergency_contact_relationship || null,
                        emergency_contact_phone: employeeData.emergency_contact_phone || null,
                        
                        // Bank Details (optional)
                        bank_name: employeeData.bank_name || null,
                        bank_account_number: employeeData.bank_account_number || null,
                        bank_ifsc: employeeData.bank_ifsc || null,
                        
                        // Set default role and status
                        role_id: employeeData.role_id || null,
                        probation_policy: employeeData.probation_policy || null,
                        notice_period: employeeData.notice_period || null,
                        holiday_plan_id: employeeData.holiday_plan_id || null,
                        shift_id: employeeData.shift_id || employeeData.shift || null,
                        weekly_off: employeeData.weekly_off || null,
                        attendance_number: employeeData.attendance_number || null,
                        attendance_capture_scheme: employeeData.attendance_capture_scheme || null,
                        attendance_tracking_policy: employeeData.attendance_tracking_policy || null,
                        leave_plan: employeeData.leave_plan || null,
                        overtime_policy: employeeData.overtime || null,
                        
                        // Compensation (optional)
                        ctc: employeeData.ctc ? parseFloat(employeeData.ctc) : null,
                        currency: employeeData.currency || 'INR',
                        effective_date: employeeData.effective_date || null,
                        payroll_frequency: employeeData.payroll_frequency || 'Monthly',
                        
                        // Tax Information (optional)
                        panNumber: employeeData.panNumber || null,
                        uanNumber: employeeData.uanNumber || null,
                        
                        // Salary Structure (optional)
                        basicSalary: employeeData.basicSalary ? parseFloat(employeeData.basicSalary) : null,
                        hra: employeeData.hra ? parseFloat(employeeData.hra) : null,
                        specialAllowance: employeeData.specialAllowance ? parseFloat(employeeData.specialAllowance) : null,
                        variablePay: employeeData.variablePay ? parseFloat(employeeData.variablePay) : null
                    };
                });

                const response = await employeeService.bulkImportEmployees(transformedEmployees);
                console.log('✅ Bulk import successful!');
                console.log('📥 Response from backend:', response);
                
                // Force refresh from backend to get the updated employee list
                await fetchEmployees(true);

                // Notify all components about bulk import
                notifyEmployeeUpdate('bulk_imported', {
                    imported: response.imported || employeesData.length,
                    failed: response.failed || 0,
                    total: employeesData.length
                });

                return {
                    success: true,
                    imported: response.imported || employeesData.length,
                    failed: response.failed || 0,
                    errors: response.errors || [],
                    total: employeesData.length
                };
            } catch (error) {
                console.error('❌ Backend bulk import FAILED!');
                console.error('Error details:', error);
                console.error('⚠️ Falling back to localStorage (will NOT persist on refresh)');
            }
        }

        // Fallback to localStorage (legacy/offline mode)
        const newEmployees = [];
        const currentEmployees = [...employees];

        employeesData.forEach((employeeData, index) => {
            const newId = `EMP${String(currentEmployees.length + newEmployees.length + 1).padStart(3, '0')}`;
            const manager = currentEmployees.find(emp => emp.id === employeeData.manager_id);

            const newEmployee = {
                id: newId,
                name: `${employeeData.first_name} ${employeeData.last_name}`,
                first_name: employeeData.first_name,
                last_name: employeeData.last_name,
                employee_code: newId,
                department: departments.find(d => d.id === employeeData.department_id)?.name || '',
                department_id: employeeData.department_id,
                designation: employeeData.designation,
                email: employeeData.email,
                phone: employeeData.phone || '',
                joiningDate: employeeData.joining_date,
                joining_date: employeeData.joining_date,
                exitDate: null,
                employment_status: 'active',
                employment_type: employeeData.employment_type || 'full-time',
                managerId: employeeData.manager_id || null,
                managerName: manager ? manager.name : null,
                role: 'employee',
                photo: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 50) + 1}`,
                payPackage: [], // Start with empty payroll data
                // HR Portal Login Credentials
                loginCredentials: {
                    loginId: employeeData.email,
                    password: 'oakhr123',
                    createdAt: new Date().toISOString()
                },
                accessLevel: employeeData.accessLevel || 'employee' // Default to employee access
            };

            newEmployees.push(newEmployee);
        });

        setEmployees(prev => {
            const updated = [...prev, ...newEmployees];
            // Notify all components about bulk import
            notifyEmployeeUpdate('bulk_imported', {
                imported: newEmployees.length,
                failed: 0,
                total: employeesData.length,
                source: 'localStorage'
            });
            return updated;
        });
        return {
            success: true,
            imported: newEmployees.length,
            failed: 0,
            errors: [],
            total: employeesData.length,
            source: 'localStorage'
        };
    };

    // Update employee
    const updateEmployee = async (employeeId, updates) => {
        const authToken = localStorage.getItem('authToken');
        const currentUser = getCurrentUser();
        const normalizedRole = getNormalizedRole(currentUser);
        const isAdminUser = normalizedRole === 'admin' || normalizedRole === 'superadmin';
        const targetEmployee = employees.find(emp => emp.id === employeeId);

        // Clear cache before update to ensure fresh data
        sessionCache.clear('employees');

        let filteredUpdatesForFallback = null;

        // Transform compensation fields into pay_package structure
        // This is needed because the backend stores these in a JSONB field
        if (updates.ctc || updates.currency || updates.payroll_frequency || updates.effective_date) {
            const existingPayPackage = targetEmployee?.pay_package || {};

            updates.pay_package = {
                ...existingPayPackage,
                ctc: updates.ctc ? parseFloat(updates.ctc) : existingPayPackage.ctc,
                currency: updates.currency || existingPayPackage.currency || 'INR',
                annual_ctc: updates.ctc ? parseFloat(updates.ctc) : existingPayPackage.annual_ctc,
                payroll_frequency: updates.payroll_frequency || existingPayPackage.payroll_frequency || 'Monthly',
                effective_date: updates.effective_date || existingPayPackage.effective_date
            };

            // Remove flat compensation fields as they're now in pay_package
            delete updates.ctc;
            delete updates.currency;
            delete updates.payroll_frequency;
            delete updates.effective_date;
        }

        // Authenticated flow
        if (authToken) {
            // Admins can update directly via backend
            if (isAdminUser) {
                try {
                    const updatedEmployee = await employeeService.updateEmployee(employeeId, updates);
                    await fetchEmployees(true);
                    // Notify all components about the update
                    notifyEmployeeUpdate('updated', updatedEmployee);

                    // Refresh localStorage if admin updated their own record
                    const storedUserRaw = localStorage.getItem('user');
                    if (storedUserRaw) {
                        try {
                            const storedUser = JSON.parse(storedUserRaw);
                            if (storedUser?.id === employeeId || storedUser?.email === targetEmployee?.email) {
                                const updatedUser = {
                                    ...storedUser,
                                    ...updates,
                                    secondary_access: updates.secondaryAccess || updates.secondary_access || storedUser.secondary_access,
                                    secondaryAccess: updates.secondaryAccess || updates.secondary_access || storedUser.secondaryAccess,
                                };
                                localStorage.setItem('user', JSON.stringify(updatedUser));
                                window.dispatchEvent(new Event('storage'));
                            }
                        } catch (e) {
                            console.error('Error refreshing stored user:', e);
                        }
                    }

                    return updatedEmployee;
                } catch (error) {
                    console.error('Backend updateEmployee failed for admin:', error);
                    throw error;
                }
            }

            // Non-admin users submit an approval request instead of direct update
            if (currentUser) {
                const changeSummary = buildChangeSummary(targetEmployee, updates, APPROVAL_ALLOWED_FIELDS);

                if (changeSummary.length === 0) {
                    return {
                        success: true,
                        pendingApproval: false,
                        message: 'No changes detected'
                    };
                }

                const filteredUpdates = changeSummary.reduce((acc, item) => {
                    acc[item.field] = updates[item.field];
                    return acc;
                }, {});
                filteredUpdatesForFallback = filteredUpdates;

                const submissionPayload = {
                    employee_id: employeeId,
                    employee_name: targetEmployee?.name
                        || `${targetEmployee?.first_name || ''} ${targetEmployee?.last_name || ''}`.trim(),
                    submitted_by: currentUser.id,
                    submitted_by_name: currentUser.name
                        || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
                        || 'Employee',
                    submitted_by_role: normalizedRole || 'employee',
                    submitted_at: new Date().toISOString(),
                    changes: changeSummary,
                    updates: filteredUpdates,
                    previous_values: changeSummary.reduce((acc, item) => {
                        acc[item.field] = item.from;
                        return acc;
                    }, {}),
                    metadata: {
                        source: 'employee_self_service'
                    }
                };

                const response = await approvalService.submitEmployeeUpdateRequest(submissionPayload);

                return {
                    success: response?.success !== false,
                    pendingApproval: true,
                    request: response?.request || response?.data || response,
                    message: 'Changes submitted for admin approval'
                };
            }

            // If we cannot determine user context, attempt backend update
            try {
                const updatedEmployee = await employeeService.updateEmployee(employeeId, updates);
                await fetchEmployees(true);
                return updatedEmployee;
            } catch (error) {
                console.error('Backend updateEmployee failed without user context, falling back to local update:', error);
            }
        }

        // Fallback to local state/localStorage updates (legacy/offline)
        let updatedRecord = null;
        const updatesToApply = filteredUpdatesForFallback && Object.keys(filteredUpdatesForFallback).length > 0
            ? filteredUpdatesForFallback
            : updates;

        setEmployees(prev => {
            const updated = prev.map(emp => {
                if (emp.id === employeeId) {
                    const manager = updatesToApply.managerId ? prev.find(e => e.id === updatesToApply.managerId) : null;
                    const mergedEmployee = {
                        ...emp,
                        ...updatesToApply,
                        name: updatesToApply.first_name && updatesToApply.last_name
                            ? `${updatesToApply.first_name} ${updatesToApply.last_name}`
                            : emp.name,
                        department: updatesToApply.department_id
                            ? departments.find(d => d.id === updatesToApply.department_id)?.name || emp.department
                            : emp.department,
                        designation: updatesToApply.designation || emp.designation,
                        managerName: manager ? manager.name : (updatesToApply.managerId === '' ? null : emp.managerName)
                    };

                    // If this is the currently logged-in user, update localStorage
                    const storedUserRaw = localStorage.getItem('user');
                    if (storedUserRaw) {
                        try {
                            const storedUser = JSON.parse(storedUserRaw);
                            if (storedUser?.id === employeeId) {
                                const updatedUser = {
                                    ...storedUser,
                                    ...updatesToApply,
                                    secondaryAccess: updatesToApply.secondaryAccess || storedUser.secondaryAccess
                                };
                                localStorage.setItem('user', JSON.stringify(updatedUser));
                                window.dispatchEvent(new Event('storage'));
                            }
                        } catch (error) {
                            console.error('Error updating stored user after local employee update:', error);
                        }
                    }

                    updatedRecord = mergedEmployee;
                    return mergedEmployee;
                }
                return emp;
            });

            // Notify all components about the update
            if (updatedRecord) {
                notifyEmployeeUpdate('updated', updatedRecord);
            }

            return updated;
        });

        return {
            success: true,
            pendingApproval: false,
            request: updatedRecord,
            message: 'Employee updated locally (offline mode)'
        };
    };

    // Delete employee
    const deleteEmployee = async (employeeId) => {
        const authToken = localStorage.getItem('authToken');

        // Try backend API first
        if (authToken) {
            try {
                await employeeService.deleteEmployee(employeeId);

                // Update local state
                setEmployees(prev => prev.filter(emp => emp.id !== employeeId));

                return { success: true };
            } catch (error) {
                console.error('Backend deleteEmployee failed, using localStorage:', error);
            }
        }

        // Fallback to localStorage
        setEmployees(prev => {
            const updated = prev.filter(emp => emp.id !== employeeId);
            // Notify all components about the deletion
            notifyEmployeeUpdate('deleted', { id: employeeId });
            return updated;
        });
    };

    // Get potential managers (employees who can be managers)
    const getPotentialManagers = (excludeId = null) => {
        return employees.filter(emp =>
            emp.id !== excludeId &&
            (emp.role === 'admin' || emp.role === 'manager' || (emp.designation && emp.designation.toLowerCase().includes('manager')))
        );
    };

    // Get employees without managers
    const getEmployeesWithoutManagers = () => {
        return employees.filter(emp => !emp.managerId && emp.role !== 'admin');
    };

    // Clear all employees (for testing/reset purposes)
    const clearAllEmployees = () => {
        setEmployees([]);
        // Clear session cache as well
        sessionCache.clear('employees');
    };

    const value = {
        employees,
        loading,
        currentEmployee,
        departments,
        getEmployeeAddRestriction,
        checkEmployeeAddRestriction,
        fetchEmployees,
        addEmployee,
        bulkAddEmployees,
        updateEmployee,
        deleteEmployee,
        generateEmployeeId,
        getPotentialManagers,
        getEmployeesWithoutManagers,
        clearAllEmployees,
        onEmployeeUpdate
    };

    return (
        <EmployeeContext.Provider value={value}>
            {children}
        </EmployeeContext.Provider>
    );
};
